/**
 * Preloader control — full-viewport dim overlay + spinner.
 *
 * Markup contract: `_Preloader.cshtml` renders a `[data-amri-preloader]`
 * element into the layout on every page. Element starts hidden
 * (`style="display:none;"`); `showPreloader()` clears the inline
 * `display` so the CSS `.amri-preloader { display: flex }` takes
 * over, and `hidePreloader()` re-sets `display: none`.
 *
 * Two use patterns:
 *   1. Any `<form>` element carrying `data-amri-preloader-on-submit`
 *      triggers `showPreloader()` on `submit` — matches the legacy
 *      `common.setPreloaderBeforeFormSubmission(formSelector)` helper
 *      used by legacy checkout / account forms.
 *   2. Any TS code can `import { showPreloader, hidePreloader } from
 *      '.../preloader';` and call them directly for AJAX flows.
 *
 * On `pageshow` with `event.persisted === true` (bfcache back/forward
 * navigation restoring a form-post page), we auto-hide so the user
 * doesn't see the preloader stuck on when navigating back to a page
 * that was in-flight when they left.
 */

const PRELOADER_HOOK = '[data-amri-preloader]';
const AUTO_SUBMIT_HOOK = 'form[data-amri-preloader-on-submit]';

function getPreloader(): HTMLElement | null {
    return document.querySelector<HTMLElement>(PRELOADER_HOOK);
}

export function showPreloader(): void {
    const el = getPreloader();
    if (el === null) {
        return;
    }
    el.style.display = '';
}

export function hidePreloader(): void {
    const el = getPreloader();
    if (el === null) {
        return;
    }
    el.style.display = 'none';
}

export function initPreloader(): void {
    document.querySelectorAll<HTMLFormElement>(AUTO_SUBMIT_HOOK).forEach((form) => {
        form.addEventListener('submit', () => {
            showPreloader();
        });
    });

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
            hidePreloader();
        }
    });
}
